import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Download, MapPin, ShieldCheck, Check } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "../components/Motion";
import { StatusBadge, ProjectCard } from "../components/ProjectCard";
import { getProject, PROJECTS, SITE, waLink } from "../lib/data";

const NotFound = () => (
  <div className="pt-44 pb-40 text-center" data-testid="project-not-found">
    <h1 className="font-serif-lux text-6xl text-[#1A1A1A]">Project not found</h1>
    <Link to="/projects" className="inline-block mt-8 overline text-[#C19A6B] link-sweep">← Back to projects</Link>
  </div>
);

export default function ProjectDetail() {
  const { slug } = useParams();
  const project = getProject(slug);
  const [activeImg, setActiveImg] = useState(0);

  if (!project) return <NotFound />;

  const downloadBrochure = () => {
    if (project.brochure) {
      window.open(project.brochure, "_blank", "noopener,noreferrer");
      toast.success("Opening brochure", { description: `${project.name} — official PDF` });
      return;
    }
    const content = `${project.name}\n${project.builder} · ${project.location}\n\nType: ${project.type}\nStatus: ${project.status}\nPrice: ${project.price}\nRERA: ${project.rera}\n\n${project.description}\n\nHighlights:\n${project.highlights.map((h) => "- " + h).join("\n")}\n${project.pricingNote ? "\nPricing note: " + project.pricingNote : ""}\n\nEnquiries: ${SITE.name} · ${SITE.phone} · ${SITE.email}`;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${project.slug}-brochure.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Brochure downloaded", { description: "A detailed PDF brochure can be requested via WhatsApp." });
  };

  const others = PROJECTS.filter((p) => p.slug !== project.slug).slice(0, 2);

  return (
    <div data-testid="project-detail-page" className="pt-28 md:pt-36">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Link to="/projects" data-testid="back-to-projects" className="inline-flex items-center gap-2 overline text-[#5C5C5C] hover:text-[#1A1A1A] mb-10">
          <ArrowLeft size={15} /> All projects
        </Link>

        {/* Title row */}
        <div className="grid md:grid-cols-12 gap-6 items-end border-b border-[#E5DFD3] pb-10">
          <div className="md:col-span-8">
            <div className="flex items-center gap-4 mb-4">
              <p className="overline text-[#C19A6B]">{project.city}</p>
              <StatusBadge status={project.status} />
            </div>
            <h1 className="font-serif-lux text-[#1A1A1A] text-6xl md:text-8xl leading-[0.9] tracking-tight">{project.name}</h1>
            <p className="font-sans-lux text-[#5C5C5C] mt-4">{project.builder} · {project.location}</p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <p className="overline text-[#5C5C5C]">Starting</p>
            <p className="font-serif-lux text-4xl text-[#1A1A1A] mt-1">{project.price}</p>
          </div>
        </div>
      </div>

      {/* Gallery */}
      <section data-testid="project-gallery" className="max-w-[1400px] mx-auto px-6 md:px-10 mt-14">
        <div className="img-zoom-wrap border border-[#E5DFD3] relative grain">
          <motion.img
            key={activeImg}
            initial={{ opacity: 0.4 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            src={project.gallery[activeImg]}
            alt={`${project.name} view ${activeImg + 1}`}
            className="w-full aspect-[16/9] object-cover"
          />
        </div>
        <div className="grid grid-cols-4 gap-3 md:gap-6 mt-4">
          {project.gallery.map((g, i) => (
            <button
              key={i}
              data-testid={`gallery-thumb-${i}`}
              onClick={() => setActiveImg(i)}
              className={`img-zoom-wrap border aspect-[4/3] overflow-hidden transition-opacity ${
                activeImg === i ? "border-[#C19A6B]" : "border-[#E5DFD3] opacity-70 hover:opacity-100"
              }`}
            >
              <img src={g} alt={`thumbnail ${i + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </section>

      {/* Body: description + sticky sidebar */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-7">
          <Reveal>
            <p className="overline text-[#C19A6B] mb-4">Overview</p>
            <p className="font-serif-lux text-3xl md:text-4xl leading-[1.25] text-[#1A1A1A] tracking-tight">
              {project.tagline}
            </p>
            <p className="font-sans-lux text-[#5C5C5C] mt-8 leading-relaxed">{project.description}</p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="grid grid-cols-2 gap-y-6 gap-x-8 mt-12">
              {project.highlights.map((h) => (
                <div key={h} className="flex items-start gap-3">
                  <Check size={18} className="text-[#6E7765] mt-1 shrink-0" />
                  <span className="font-sans-lux text-[#1A1A1A]">{h}</span>
                </div>
              ))}
            </div>
          </Reveal>

          {project.pricingNote && (
            <Reveal delay={0.15}>
              <p className="mt-10 p-5 bg-[#F5F2EB] border border-[#E5DFD3] font-sans-lux text-sm text-[#5C5C5C]">
                {project.pricingNote}
              </p>
            </Reveal>
          )}

          {/* Video */}
          <Reveal delay={0.1}>
            <div className="mt-16" data-testid="project-video">
              <p className="overline text-[#C19A6B] mb-4">Walkthrough</p>
              <div className="aspect-video border border-[#E5DFD3]">
                <iframe
                  src={project.video}
                  title={`${project.name} walkthrough`}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </Reveal>

          {/* Map */}
          <Reveal delay={0.1}>
            <div className="mt-16" data-testid="project-map">
              <p className="overline text-[#C19A6B] mb-4 flex items-center gap-2"><MapPin size={14} /> Location</p>
              <div className="aspect-[16/9] border border-[#E5DFD3]">
                <iframe
                  title={`${project.name} map`}
                  src={`https://www.google.com/maps?q=${encodeURIComponent(project.mapQuery)}&output=embed`}
                  className="w-full h-full grayscale"
                  loading="lazy"
                />
              </div>
            </div>
          </Reveal>
        </div>

        {/* Sticky sidebar */}
        <div className="md:col-span-5">
          <div className="md:sticky md:top-28 border border-[#E5DFD3] bg-[#F5F2EB] p-8">
            <div className="flex items-center gap-2 text-[#6E7765] mb-6">
              <ShieldCheck size={18} />
              <span className="overline">RERA</span>
            </div>
            <p data-testid="project-rera" className="font-sans-lux text-[#1A1A1A] break-words">{project.rera}</p>
            {project.rearaCertificate && (
              <a
                href={project.rearaCertificate}
                target="_blank"
                rel="noreferrer"
                data-testid="rera-certificate-link"
                className="inline-flex items-center gap-2 mt-3 overline text-[#C19A6B] link-sweep"
              >
                <Download size={13} /> View RERA certificate
              </a>
            )}

            <div className="h-px bg-[#E5DFD3] my-6" />
            <dl className="space-y-4 font-sans-lux text-sm">
              <div className="flex justify-between"><dt className="text-[#5C5C5C]">Type</dt><dd className="text-[#1A1A1A] text-right">{project.type}</dd></div>
              <div className="flex justify-between"><dt className="text-[#5C5C5C]">Status</dt><dd className="text-[#1A1A1A]">{project.status}</dd></div>
              <div className="flex justify-between"><dt className="text-[#5C5C5C]">Price</dt><dd className="text-[#1A1A1A] text-right">{project.price}</dd></div>
            </dl>

            <button
              data-testid="download-brochure-button"
              onClick={downloadBrochure}
              className="w-full mt-8 flex items-center justify-center gap-3 bg-[#1A1A1A] text-[#FDFBF7] btn-lux btn-lux-invert px-6 py-4 overline"
            >
              <Download size={16} /> Download Brochure
            </button>
            <Link
              to="/contact"
              data-testid="detail-book-visit"
              className="w-full mt-3 flex items-center justify-center gap-3 border border-[#1A1A1A] text-[#1A1A1A] hover:text-[#FDFBF7] btn-lux px-6 py-4 overline"
            >
              Book a Site Visit <ArrowUpRight size={15} />
            </Link>
            <a
              href={waLink(`Hi Sambhav, I'm interested in ${project.name} (${project.location}). Please share details.`)}
              target="_blank"
              rel="noreferrer"
              data-testid="detail-whatsapp"
              className="w-full mt-3 flex items-center justify-center gap-3 text-[#1FA855] border border-[#1FA855] px-6 py-4 overline hover:bg-[#1FA855] hover:text-white transition-colors"
            >
              Chat on WhatsApp
            </a>
            {(project.priceListUrl || project.floorPlanUrl) && (
              <div className="mt-6 pt-6 border-t border-[#E5DFD3] space-y-3">
                <p className="overline text-[#5C5C5C]">More resources</p>
                {project.priceListUrl && (
                  <a href={project.priceListUrl} target="_blank" rel="noreferrer" data-testid="price-list-link"
                    className="flex items-center gap-2 font-sans-lux text-sm text-[#1A1A1A] link-sweep">
                    <Download size={14} className="text-[#C19A6B]" /> Price list (PDF)
                  </a>
                )}
                {project.floorPlanUrl && (
                  <a href={project.floorPlanUrl} target="_blank" rel="noreferrer" data-testid="floor-plan-link"
                    className="flex items-center gap-2 font-sans-lux text-sm text-[#1A1A1A] link-sweep">
                    <Download size={14} className="text-[#C19A6B]" /> Floor plan (PDF)
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* More projects */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-32">
        <div className="border-t border-[#E5DFD3] pt-10 mb-12">
          <h2 className="font-serif-lux text-4xl md:text-5xl text-[#1A1A1A] tracking-tight">More projects</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-12">
          {others.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.1}>
              <ProjectCard project={p} />
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
